// imports
import {__} from "@wordpress/i18n";

// Mental Calculation Trainer elements
const mentalCalculatorContainer = document.querySelector(".mental-calculator-container");
const questionsContainer = document.querySelector(".questions");
const answerInput = document.getElementById("answerInput");
const resultsContainer = document.querySelector(".results-container");
const resultsContent = document.querySelector(".results-content");

// Variables to hold exercise state
let questions = [];
let currentExercise = [];
let currentQuestionIndex = 0;
let timer = 0;
let attempt = 0;
let answers = [];

const operatorSymbols = {
	"addition": "+",
	"multiplication": "×",
	"division": "÷",
	"subtraction": "-"
};

/**
 * Starts the exercise, generates the questions, and displays the first question.
 */
export function startExercise(firstNumberDifficultySelector, secondNumberDifficultySelector, problemOperatorSelector) {
	const firstNumberDifficulty = parseInt(firstNumberDifficultySelector.value);
	const secondNumberDifficulty = parseInt(secondNumberDifficultySelector.value);
	const operator = operatorSymbols[problemOperatorSelector.value];
	const problemQuantity = parseInt(document.getElementById("problemQuantityCount").value);

	// Store initial parameters in currentExercise for easy access later
	currentExercise = {
		operator: problemOperatorSelector.value,
		firstOperandDigits: firstNumberDifficulty,
		secondOperandDigits: secondNumberDifficulty,
	};

	mentalCalculatorContainer.style.display = "none";
	questionsContainer.style.display = "flex";

	questions = generateQuestions(firstNumberDifficulty, secondNumberDifficulty, operator, problemQuantity);
	displayQuestion();
	startTimer();
}

/**
 * Generates a list of random math questions based on the given difficulty and operator.
 */
function generateQuestions(firstDifficulty, secondDifficulty, operator, quantity) {
	const questions = [];
	for (let i = 0; i < quantity; i++) {
		const firstOperand = generateOperand(firstDifficulty);
		const secondOperand = generateOperand(secondDifficulty);
		questions.push({ firstOperand, secondOperand, operator });
	}
	return questions;
}

/**
 * Generates a random operand based on the given difficulty.
 */
function generateOperand(difficulty) {
	const min = Math.pow(10, difficulty - 1); // Minimum value with `difficulty` digits
	const max = Math.pow(10, difficulty) - 1; // Maximum value with `difficulty` digits
	return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Displays the current question on the UI.
 */
function displayQuestion() {
	const question = questions[currentQuestionIndex];
	document.querySelector(".question-number").innerText = currentQuestionIndex + 1;
	document.querySelector(".total-questions").innerText = questions.length;
	document.querySelector(".first-operand").innerText = question.firstOperand;
	document.querySelector(".operator").innerText = question.operator;
	document.querySelector(".second-operand").innerText = question.secondOperand;
	answerInput.value = "";
	answerInput.focus();
}

/**
 * Timer utility object to track elapsed time.
 */
function startTimer() {
	timer = createTimer();
	timer.start();
}

/** Stops the currently running question timer. */
function stopTimer() {
	timer.stop();
}

/**
 * Factory function that creates a timer object.
 * The timer tracks elapsed time from when start() is called.
 * @returns {{ start: Function, stop: Function, timeElapsed: Function }}
 */
function createTimer() {
	let startTime = 0;
	let intervalId;

	return {
		start() {
			startTime = Date.now();
			intervalId = setInterval(updateTimeDisplay, 1000);
		},
		stop() {
			clearInterval(intervalId);
		},
		timeElapsed() {
			return (Date.now() - startTime) / 1000;
		}
	};
}

/** Updates the on-screen timer display (MM:SS format) every second. */
function updateTimeDisplay() {
	const elapsedTime = Math.floor(timer.timeElapsed());
	const minutes = Math.floor(elapsedTime / 60).toString().padStart(2, '0');
	const seconds = (elapsedTime % 60).toString().padStart(2, '0');
	document.querySelector(".time").innerText = `${minutes}:${seconds}`;
}

/**
 * Processes the user's answer and provides feedback (correct/incorrect).
 */
export function processUserAnswer() {
	const userAnswer = parseFloat(parseFloat(answerInput.value).toFixed(2));
	const currentQuestion = questions[currentQuestionIndex];
	const correctAnswer = calculateCorrectAnswer(currentQuestion);

	if (isNaN(userAnswer)) {
		animateVibration();
	} else if (userAnswer === correctAnswer) {
		handleCorrectAnswer(userAnswer);
	} else if (attempt === 1) {
		handleIncorrectAnswer(userAnswer);
	} else {
		// First attempt failed, allow one more attempt
		animateVibration();
		recordAnswer(userAnswer);
		attempt++; // Set for next attempt
	}
}

/**
 * Calculates the correct answer for the current question.
 */
function calculateCorrectAnswer({ firstOperand, secondOperand, operator }) {
	switch (operator) {
		case "+": return firstOperand + secondOperand;
		case "-": return firstOperand - secondOperand;
		case "×": return firstOperand * secondOperand;
		case "÷": return parseFloat((firstOperand / secondOperand).toFixed(2));
		default: console.error("Unknown operator type");
	}
}

/**
 * Handles when the user submits a correct answer.
 */
function handleCorrectAnswer(userAnswer) {
	animatePulse();
	stopTimer();
	recordAnswer(userAnswer);
	attempt = 0; // Reset attempt for the next question
	if (currentQuestionIndex < questions.length - 1) {
		currentQuestionIndex++;
		displayQuestion();
		startTimer();
	} else {
		finishExercise();
	}
}

/**
 * Handles when the user submits an incorrect answer.
 */
function handleIncorrectAnswer(userAnswer) {
	animateVibration();
	stopTimer();
	recordAnswer(userAnswer);
	attempt = 0; // Reset attempt for the next question
	if (currentQuestionIndex < questions.length - 1) {
		currentQuestionIndex++;
		displayQuestion();
		startTimer();
	} else {
		finishExercise();
	}
}

/**
 * Records the user's answer for the current question.
 */
function recordAnswer(userAnswer) {
	if (!answers[currentQuestionIndex]) {
		answers[currentQuestionIndex] = { answer: [], time: 0 };
	}
	answers[currentQuestionIndex].answer.push(userAnswer);
	answers[currentQuestionIndex].time = timer.timeElapsed();
}

/**
 * Displays the results at the end of the exercise.
 */
function finishExercise() {
	questionsContainer.style.display = "none";
	resultsContainer.parentElement.style.display = "flex"; // Show results section
	resultsContent.innerHTML = formatResults();
}

/**
 * Formats the results to display on the results page.
 */
function formatResults() {
	const correctAnswersCount = calculateCorrectAnswers(); // Number of good answer
	const totalQuestions = questions.length; // Total number of questions
	const averageTime = calculateAverageTime(); // Average answer time

	const { operator, firstOperandDigits, secondOperandDigits } = currentExercise;

	let currentResultsInfo = '';
	currentResultsInfo += `<div class="current-score">`;

	// Calculate the percentage of correct answers
	const scorePercentage = Math.floor((correctAnswersCount / totalQuestions) * 100);

	// Read the results saved in the cookie (if they exist)
	const cookieData = getResultFromCookie(operator, firstOperandDigits, secondOperandDigits);
	let previousResultsInfo = '';
	if (cookieData) {
		const previousResults = cookieData;
		previousResultsInfo = `<p class="best-result">`;
		if (parseFloat(scorePercentage) > parseFloat(previousResults.scorePercentage)) {
			previousResultsInfo += `${__("Score précédent :", "superprof-gutenberg-blocks")}`;
			previousResultsInfo += `${__("Score précédent :", "superprof-gutenberg-blocks")}`;
			currentResultsInfo += `<p class="current-score-title">✨ ${__("Meilleur score !", "superprof-gutenberg-blocks")} ✨</p>`;
			saveResultsToCookie(scorePercentage, averageTime, operator, firstOperandDigits, secondOperandDigits);
		} else if (scorePercentage === previousResults.scorePercentage) {
			if (averageTime < previousResults.averageTime) {
				previousResultsInfo += `${__("Score précédent :", "superprof-gutenberg-blocks")}`;
				currentResultsInfo += `<p class="current-score-title">✨ ${__("Meilleur score !", "superprof-gutenberg-blocks")} ✨</p>`;
				saveResultsToCookie(scorePercentage, averageTime, operator, firstOperandDigits, secondOperandDigits);
			} else {
				previousResultsInfo += `${__("Meilleur score :", "superprof-gutenberg-blocks")}`;
			}
		} else {
			previousResultsInfo += `${__("Meilleur score :", "superprof-gutenberg-blocks")}`;
		}
		previousResultsInfo += ` ${previousResults.scorePercentage} % - ⏱️ ${formatTime(previousResults.averageTime)}`;
		previousResultsInfo += `</p>`;
	} else {
		currentResultsInfo += `<p class="current-score-title">✨ ${__("Meilleur score !", "superprof-gutenberg-blocks")} ✨</p>`;
		saveResultsToCookie(scorePercentage, averageTime, operator, firstOperandDigits, secondOperandDigits);
	}

	// Build HTML string of current results
	currentResultsInfo += `<p class="score-result">${correctAnswersCount}/${totalQuestions}</p>`;
	currentResultsInfo += `<p class="score-info">`;
	currentResultsInfo += `<span class="score-percentage">${scorePercentage} % - </span>`;
	currentResultsInfo += `<span class="average-time">⏱️ ${formatTime(averageTime)}<sup class="time-info-icon" aria-label="${__("Temps moyen de réponse.", "superprof-gutenberg-blocks")}">i</sup><span class="time-info">${__("Temps moyen de réponse.", "superprof-gutenberg-blocks")}</span></span>`;
	currentResultsInfo += `</p>`;
	currentResultsInfo += `</div>`;

	// Check if there are any wrong answers before showing the correction section
	let hasWrongAnswers = false;

	// Generate results for each question
	let questionResultsHTML = '';
	answers.forEach((answer, index) => {
		const question = questions[index];
		const correctAnswer = calculateCorrectAnswer(question);
		const lastAttempt = answer.answer[answer.answer.length - 1]; // Last try

		if (lastAttempt !== correctAnswer) {
			hasWrongAnswers = true; // Mark that we have at least one wrong answer
		}
	});

	// Only show the correction title if there's at least one wrong answer
	if (hasWrongAnswers) {
		questionResultsHTML += '<div class="wrong-answers">';
		questionResultsHTML += `<p class="wrong-answers-title"> ${__("Correction", "superprof-gutenberg-blocks")}</p>`;

		questionResultsHTML += answers.map((answer, index) => {
			const question = questions[index];
			const correctAnswer = calculateCorrectAnswer(question);
			const firstAttempt = answer.answer[0]; // First try
			const lastAttempt = answer.answer[answer.answer.length - 1]; // Last try

			// Show attempts if multiple answers
			const attemptsInfo = `<span class="user-answer"> ${__("Vos réponses :", "superprof-gutenberg-blocks")} ${firstAttempt} ; ${lastAttempt}</span>`;

			let resultHTML = '';
			if (lastAttempt !== correctAnswer) {
				resultHTML = `<p class='wrong-answer'><span class='answer-number'>${index + 1}</span>${question.firstOperand} ${question.operator} ${question.secondOperand} = ${correctAnswer}</br>${attemptsInfo}</p>`;
			}

			return resultHTML;
		}).join("");

		questionResultsHTML += '</div>';
	}

	return currentResultsInfo + previousResultsInfo + questionResultsHTML;
}

/**
 * Formats time from seconds to minutes:seconds.
 */
function formatTime(seconds) {
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const remainingSeconds = Math.floor(seconds % 60);
	const milliseconds = Math.floor((seconds % 1) * 100);

	if (hours > 0) {
		// If more than an hour -> 0h00min00s
		return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}s`;
	} else if (minutes > 0) {
		// If greater than 1 minute -> 00:00.00s
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}s`;
	} else {
		// If less than 1 minute -> 00.00s
		return `${remainingSeconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}s`;
	}
}

/**
 * Saves the current result (score and average time) to a cookie,
 * keyed by the exercise configuration (operator + digit counts).
 * Results are stored as a JSON object so multiple configurations can coexist.
 * The cookie is valid for 7 days.
 *
 * @param {number} scorePercentage  - The percentage of correct answers.
 * @param {number} averageTime      - The average response time in seconds.
 * @param {string} operator         - The math operator used (addition, subtraction, etc.).
 * @param {number} firstOperand     - Number of digits in the first operand.
 * @param {number} secondOperand    - Number of digits in the second operand.
 */
function saveResultsToCookie(scorePercentage, averageTime, operator, firstOperand, secondOperand) {
	// Retrieve existing data from cookie or initialize a new object if it doesn't exist
	let resultData = JSON.parse(getCookie("mentalCalculationResults") || "{}");

	// Construct a unique key for each combination
	const key = `${operator}_${firstOperand}_${secondOperand}`;

	// Update the result data for this specific combination
	resultData[key] = {
		scorePercentage: scorePercentage,
		averageTime: averageTime.toFixed(2)
	};

	// Convert to JSON and save data in a cookie (valid for 7 days)
	setCookie("mentalCalculationResults", JSON.stringify(resultData), 7);
}

/**
 * Function to set a cookie
 */
function setCookie(name, value, days) {
	let date = new Date();
	date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
	let expiration = "expires=" + date.toUTCString();
	document.cookie = name + "=" + value + ";" + expiration + ";path=/";
}

/**
 * Retrieves a previously saved result from the cookie for the given exercise configuration.
 * @param {string} operator         - The math operator (addition, subtraction, etc.).
 * @param {number} firstOperand     - Number of digits in the first operand.
 * @param {number} secondOperand    - Number of digits in the second operand.
 * @returns {{ scorePercentage: string, averageTime: string }|null}
 */
function getResultFromCookie(operator, firstOperand, secondOperand) {
	// Parse the cookie data to retrieve the JSON object
	const resultData = JSON.parse(getCookie("mentalCalculationResults") || "{}");

	// Construct the unique key
	const key = `${operator}_${firstOperand}_${secondOperand}`;

	// Return the stored result if it exists, or null if not
	return resultData[key] || null;
}

/**
 * Function to get the value of a cookie
 */
function getCookie(name) {
	let nameEq = name + "=";
	let cookiesArray = document.cookie.split(';');
	for (let i = 0; i < cookiesArray.length; i++) {
		let cookie = cookiesArray[i].trim();
		if (cookie.indexOf(nameEq) === 0) {
			return decodeURIComponent(cookie.substring(nameEq.length, cookie.length));
		}
	}
	return null;
}

/**
 * Function to calculate the number of correct answers
 */
function calculateCorrectAnswers() {
	return answers.reduce((count, answer, index) => {
		const question = questions[index];
		const correctAnswer = calculateCorrectAnswer(question);
		const lastAttempt = answer.answer[answer.answer.length - 1];
		return lastAttempt === correctAnswer ? count + 1 : count;
	}, 0);
}

/**
 * Function to calculate average response time
 */
function calculateAverageTime() {
	const totalTime = answers.reduce((total, answer) => total + answer.time, 0);
	return totalTime / answers.length;
}

/**
 * Animates the input field to indicate a correct answer.
 */
function animatePulse() {
	answerInput.classList.add("pulse");
	setTimeout(() => answerInput.classList.remove("pulse"), 500);
}

/**
 * Animates the input field to indicate an incorrect answer.
 */
function animateVibration() {
	answerInput.classList.add("vibrate");
	setTimeout(() => answerInput.classList.remove("vibrate"), 500);
}

/**
 * Resets the exercise state and UI to the initial state.
 */
export function resetExerciseState() {
	currentQuestionIndex = 0;
	answers = [];
	mentalCalculatorContainer.style.display = "flex";
	questionsContainer.style.display = "none";
	resultsContainer.parentElement.style.display = "none";
}
